/**
 * Image compression and WebP conversion utility
 * Compresses uploaded images and converts them to .webp format
 */

import { heicTo } from 'heic-to';

export interface CompressionOptions {
  /** WebP quality 0–1 (default 0.82) */
  quality?: number;
  /** Max width in pixels (maintains aspect ratio) */
  maxWidth?: number;
  /** Max height in pixels (maintains aspect ratio) */
  maxHeight?: number;
}

const SUPPORTED_IMAGE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/bmp',
  'image/heic',
  'image/heif',
];

const HEIC_TYPES = ['image/heic', 'image/heif'];

function isHeicFile(file: File): boolean {
  return HEIC_TYPES.includes(file.type) || /\.heic$/i.test(file.name) || /\.heif$/i.test(file.name);
}

/**
 * Check if a file is a supported image type
 */
export function isSupportedImageType(file: File): boolean {
  return SUPPORTED_IMAGE_TYPES.includes(file.type) || isHeicFile(file);
}

/**
 * Convert HEIC/HEIF to a format the browser can draw (JPEG)
 * Uses heic-to (libheif 1.21.2) for better support of newer iPhone HEIC formats
 */
async function convertHeicToJpeg(file: File): Promise<Blob> {
  const blob = await heicTo({
    blob: file,
    type: 'image/jpeg',
    quality: 0.95,
  });
  if (!blob) throw new Error('HEIC conversion failed');
  return blob;
}

/**
 * Compress an image and convert to WebP format
 * Uses Canvas API - runs entirely in the browser
 * Supports HEIC/HEIF via heic-to (converts to JPEG first)
 */
export async function compressImageToWebP(
  file: File,
  options: CompressionOptions = {}
): Promise<Blob> {
  const { quality = 0.82, maxWidth, maxHeight } = options;

  // HEIC must be converted to JPEG first (browser can't draw HEIC on canvas)
  let sourceBlob: Blob = file;
  if (isHeicFile(file)) {
    sourceBlob = await convertHeicToJpeg(file);
  }

  return new Promise((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const objectUrl = URL.createObjectURL(sourceBlob);

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      let width = img.width;
      let height = img.height;

      // Resize if max dimensions specified
      if (maxWidth || maxHeight) {
        const maxW = maxWidth ?? Infinity;
        const maxH = maxHeight ?? Infinity;
        if (width > maxW || height > maxH) {
          const scale = Math.min(maxW / width, maxH / height);
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }
      }

      canvas.width = width;
      canvas.height = height;

      if (!ctx) {
        reject(new Error('Could not get canvas context'));
        return;
      }

      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('WebP conversion failed'));
          }
        },
        'image/webp',
        quality
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Failed to load image'));
    };
    img.src = objectUrl;
  });
}

/**
 * Create a filename for the WebP output (preserves base name)
 */
export function getWebpFilename(originalName: string): string {
  const base = originalName.replace(/\.[^.]+$/, '');
  return `${base}.webp`;
}
